<script lang="ts" setup>
import type { DynamicAuctionItemList } from '@autobid/strapi-integration/rest/components'
import CurrentAuctionButton from '@autobid/ui/components/elements/car/CurrentAuctionButton.vue'
import { useSpecialConditionNotification } from '@autobid/ui/composables/auction/useSpecialConditionNotification'
import { useCarCountdown } from '@autobid/ui/composables/car/countdown/useCarCountdown'
import { useAuctionCars } from '@autobid/ui/composables/car/data/useAuctionCars'
import { useCurrentCarAnnouncement } from '@autobid/ui/composables/car/data/useCurrentCarAnnouncement'
import { useNoFilterCounters } from '@autobid/ui/composables/car/data/useNoFilterCounters'
import { useCarsListUrlFilter } from '@autobid/ui/composables/carsList/useFilter'
import { useAuction } from '@autobid/ui/composables/useAuction'
import { useAuctionId } from '@autobid/ui/composables/useAuctionId'
import { useDictionary } from '@autobid/ui/composables/useDictionary'
import { useIsMobile } from '@autobid/ui/composables/useIsMobile'
import { provideFilterComposable } from '@autobid/ui/utils/carsList/provideFilterComposable'
import { computed, inject, onServerPrefetch } from 'vue'

const auctionStrapiData = inject<DynamicAuctionItemList>('auction-strapi-data')

const useFilter = () => useCarsListUrlFilter()
provideFilterComposable(useFilter)

const { reset } = useFilter()
const isMobile = useIsMobile()
const auctionId = useAuctionId() as Ref<number> // it may be undefined only if someone tries to use it in the wrong page in the strapi (this case MUSTN'T never happen). The value may be also undefined for other page types e.g. barometer, for this type of page it's going to be number in this place
const {
  data: cars,
  error,
  isFetching,
  refetch,
  errorUpdateCount,
  suspense: suspenseCars,
} = useAuctionCars(auctionId, auctionStrapiData?.itemsPerPage || 20, {
  keepPreviousData: true,
})

const { showSpecialConditionNotificationDialog, doesAuctionMeetConditions }
  = useSpecialConditionNotification({ auctionId: auctionId.value })
const { auction } = useAuction(auctionId.value)

const renderAnnouncementCar = computed(() => {
  if (auction.value?.stage === 'FINISHED')
    return false

  return isMobile.value
})

const { data: announcementCar, suspense: suspenseAnnouncement }
  = useCurrentCarAnnouncement(auctionId, {
    enabled: renderAnnouncementCar,
  })

const { suspense: suspenseNoFilterCounters } = useNoFilterCounters('auction')
const { suspense: suspenseDictionary } = useDictionary({ type: 'auctions' })
async function dataFetchers() {
  return await Promise.all([
    suspenseNoFilterCounters(),
    suspenseDictionary(),
    suspenseCars(),
    ...(renderAnnouncementCar.value ? [suspenseAnnouncement()] : []),
  ])
}

onServerPrefetch(dataFetchers)
await dataFetchers()

const {
  timeRemaining: hotbidTimeRemaining,
  isCountdownOver: isHotBidCountdownOver,
} = useCarCountdown(announcementCar)

const bodyClass = computed(() => {
  if (announcementCar.value)
    return 'has-car-list-in-auction'

  return 'has-car-list'
})
useHead({
  bodyAttrs: {
    class: bodyClass,
  },
})
</script>

<template>
  <div>
    <DynamicAuctionItemListInner
      v-if="cars"
      :cars="cars.items"
      :pages="cars.itemPageCount"
      :is-fetching="isFetching"
    >
      <template #leftSide>
        <DynamicAuctionItemListFilters
          :counters="cars.metadata.counters"
          :show-deliverer="
            auction?.activeStatusesNames?.includes('show_deliverer')
          "
        />
      </template>

      <template #aboveCars>
        <div
          class="flex w-full flex-col items-start md:mb-3 md:flex-row md:items-center md:justify-between"
        >
          <p class="mr-3 hidden md:block">
            {{ $t('cars-list.instructions') }}
          </p>
          <div
            class="hidden w-full flex-row items-center justify-end gap-3 md:flex md:w-auto"
          >
            <CurrentAuctionButton
              v-if="!isMobile && auction?.slug && auction.stage !== 'FINISHED'"
              :slug="auction.slug"
              class="!px-2"
            />

            <DynamicAuctionItemListSorting />
          </div>
        </div>

        <div
          v-if="announcementCar && renderAnnouncementCar"
          class="fixed bottom-14 left-0 z-40 flex w-full flex-wrap items-center justify-between gap-2 bg-primaryDarkest px-3 pb-5 pt-3 text-white md:hidden"
        >
          <CurrentAuctionButton v-if="auction?.slug" :slug="auction.slug" class="!px-2" />

          <span class="flex items-center">
            <span class="ml-1 mr-2 block text-sm sm:mr-3">
              <span v-if="announcementCar.inActiveHotbidPhase">
                {{
                  $t('shared-car.hotbid-nr', {
                    catalogNumber: announcementCar.catalogNumber,
                  })
                }}
              </span>
              <span v-else>
                {{ $t('cars-list.bid-now') }}
              </span>
            </span>
            <span
              v-if="announcementCar.inActiveHotbidPhase && !isHotBidCountdownOver"
              class="text-bold sm:text-lg"
            >
              {{ hotbidTimeRemaining.full }}
            </span>
          </span>
        </div>

        <LazySectionsAuctionSpecialConditionDialog
          v-if="doesAuctionMeetConditions"
          :auction-id="auctionId"
          :active="showSpecialConditionNotificationDialog"
          @close="showSpecialConditionNotificationDialog = false"
        />
      </template>
    </DynamicAuctionItemListInner>

    <LazyDynamicAuctionItemListErrorState
      v-else-if="error"
      :error="error"
      :error-update-count="errorUpdateCount"
      @reset="reset"
      @refetch="refetch"
    />
  </div>
</template>
